import React from 'react'
import Image from 'next/image'
import ResponsiveHOC from '@components/ui/ResponsiveHOC'
import styles from './styles.module.scss'
import { Carrier, Carriers } from '@api/returns/types'
import Label from '@components/ui/formElements/Label'
import Dropdown from '@components/ui/formElements/Dropdown'
import { SetSelectionArgs } from '@components/ui/formElements/Dropdown/List'
import { EmptyObject } from 'types/helpers'
import imageLoader from '@lib/imageLoader'
import { CollectionDate, DELIVERY_METHOD_WITH_DATE_DROPDOWN } from '..'

const carriersLogo = {
  anpost:
    'https://images.ctfassets.net/amhdwl2zsv5z/2M80ZX37bO2sNNzBZP1F0q/a1b818a4749c0015fa95f3fea987d7ec/aramex.jpg',
  correos:
    'https://images.ctfassets.net/amhdwl2zsv5z/6mR6cJogYpltKiR5yRdULy/a5c5aca213b9e93177fb75900b25701e/correos.jpg',
  dhl: 'https://images.ctfassets.net/amhdwl2zsv5z/5Worb7PSEcpXhRFrvdWK1o/07302f3bf0c4c28dd1500abdeacd2ad3/dhl.jpg',
  dpd: 'https://images.ctfassets.net/amhdwl2zsv5z/4p6HMhm6n8u52AsY8n2kBo/36eb7ce1387671d20bc12e481a21b74b/dpd.jpg',
  maltapost:
    'https://images.ctfassets.net/amhdwl2zsv5z/2EMnX1oM3wDPtDmzyERqEl/d52538263e9fcdb13eaa0d10f05f7dc2/maltapost.jpg',
  postnl:
    'https://images.ctfassets.net/amhdwl2zsv5z/4HbrqUbnHI56dHsQqnF37P/d138dfc37468189bda48a5a29b842674/postnl.jpg',
  restofworld:
    'https://images.ctfassets.net/amhdwl2zsv5z/1nGlpjlfkXlRTPQtfAXwyp/df45d3534be08897933d226c9c2cd3be/restofworld.jpg',
  'royalmail-parcelcollect':
    'https://images.ctfassets.net/amhdwl2zsv5z/76j32hNVMfKiIWsklCLRz3/fd1672ca7c63158909b636069322e007/royalmail.png',
  royalmail:
    'https://images.ctfassets.net/amhdwl2zsv5z/76j32hNVMfKiIWsklCLRz3/fd1672ca7c63158909b636069322e007/royalmail.png',
  ups: 'https://images.ctfassets.net/amhdwl2zsv5z/7F3h2GZSzvVvgkbWZfi5hY/ce19dd6e57bf665e3beda8874fd0c2f1/ups.png',
  usps: 'https://images.ctfassets.net/amhdwl2zsv5z/2Vyc2OTtJypJLqFKYmgoDh/2b00f7ee229fdaac8d16cbf9cbddb668/usps.jpg'
}

function getNextAvailableDays() {
  const today = new Date()
  const week = []

  for (let i = 0; i <= 5; i++) {
    const newDay = new Date(today.setDate(today.getDate() + 1))

    // Only push days other than Sunday
    if (newDay.getDay() !== 0) {
      const formattedDate = new Date(newDay).toISOString().slice(0, 10)
      const year = formattedDate.split('-')[0]
      const month = formattedDate.split('-')[1]
      const day = formattedDate.split('-')[2]
      week.push({
        value: formattedDate,
        label: `${day}-${month}-${year}`
      })
    }
  }
  return week
}

type DateDropdownProperties = {
  selectedDate: string | undefined
  setSelectedDate: (arg: string) => void
}

function DateDropdown({
  selectedDate,
  setSelectedDate
}: DateDropdownProperties) {
  const listOfAvailableDays = getNextAvailableDays()
  const handleDateSelection = (date: SetSelectionArgs) => {
    setSelectedDate(date['delivery-date-dropdown'] as string)
  }
  return (
    <div className={styles.dateDropdown}>
      <Label>
        Select Date<span className={styles.dateDropdown__labelAsterix}>*</span>
      </Label>
      <Dropdown
        defaultValue="Please Select"
        placeholder="Please Select"
        list={listOfAvailableDays}
        id="delivery-date-dropdown"
        selection={selectedDate}
        setSelection={handleDateSelection}
      />
    </div>
  )
}

type DeliveryOptionsProps = {
  deliveryMethods: Carriers
  carrierId?: number | string
  onChangeDeliveryMethod: (arg: Carrier | EmptyObject) => void
  selectedDeliveryMethod: (Carrier & CollectionDate) | EmptyObject
}

function getDeliveryMethodById(deliveryMethods: Carriers, id: number) {
  return deliveryMethods.find(method => method.id === id) as Carrier
}

const DeliveryOptions = ({
  deliveryMethods,
  carrierId,
  onChangeDeliveryMethod,
  selectedDeliveryMethod
}: DeliveryOptionsProps) => {
  const handleOnSelectionRadio = (id: number) => {
    const deliveryMethod = getDeliveryMethodById(deliveryMethods, id)
    onChangeDeliveryMethod(deliveryMethod)
  }

  const handleOnSelectDate = ({ id, date }: { id: number; date: string }) => {
    const deliveryMethod = getDeliveryMethodById(deliveryMethods, id)

    onChangeDeliveryMethod({
      ...deliveryMethod,
      ...(date && { collectionDate: date })
    })
  }

  return (
    <div className={styles.delivery} data-testid="delivery-options">
      {deliveryMethods.map(method => {
        const shouldShowDateDropdown =
          DELIVERY_METHOD_WITH_DATE_DROPDOWN.includes(method?.id)
        const imageSrc =
          carriersLogo[
            method.title
              .toLowerCase()
              .replace(/\s+/g, '') as keyof typeof carriersLogo
          ] || carriersLogo.restofworld
        const activeMethod = method.id === carrierId
        return (
          <div
            key={method.id}
            className={styles.delivery__container}
            onKeyDown={() => handleOnSelectionRadio(method.id)}
            onClick={() => handleOnSelectionRadio(method.id)}
            role="button"
            tabIndex={0}
          >
            <div
              className={`${styles.delivery__radio} ${
                activeMethod ? styles.delivery_radioSelected : ''
              }`}
            ></div>
            <div className={styles.delivery__imageContainer}>
              <ResponsiveHOC minWidth={600}>
                <Image
                  src={imageSrc}
                  alt={method.title}
                  width={220}
                  height={164}
                  loader={imageLoader}
                />
              </ResponsiveHOC>
            </div>
            <div className={styles.delivery__details}>
              <div className={styles.delivery__title}>
                <span className={styles.delivery__name}>{method.title}</span>
                {method.amount === 'free' && (
                  <span className={styles.delivery__amount}>FREE</span>
                )}
              </div>
              <div className={styles.delivery__byline}>{method.byline}</div>
              <div
                className={styles.delivery__desc}
                dangerouslySetInnerHTML={{ __html: method.message }}
              ></div>

              {shouldShowDateDropdown && (
                <DateDropdown
                  setSelectedDate={date =>
                    handleOnSelectDate({ id: method.id, date: date })
                  }
                  selectedDate={selectedDeliveryMethod?.collectionDate}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default DeliveryOptions
